import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import classNames from 'classnames';
import {FormInput} from '@homeaway/react-form-components';
import {SVGIcon} from '@homeaway/react-svg';
import {ARROW_RIGHT__24} from '@homeaway/svg-defs';
import {TrackingContext} from '@homeaway/react-event-tracking';
import propTypeDateString from '../utils/propTypeDateString';
import {
    getEffectiveConsolidatedDateString,
    getEffectiveDateText,
} from '../utils/dateUtils';
import isRequiredIf from '../utils/isRequiredIf';
import emitDatePickerEvent from '../utils/edap';
import DatePickerBase from '../DatePickerBase/DatePickerBase';
import './DateRangePicker.less';

const EVENT_DATES_CLEARED = 'datepicker.dates.cleared';
const EVENT_DATES_SELECTED = 'datepicker.dates.selected';
const EVENT_PICKER_CLOSE = 'datepicker.close';
const EVENT_PICKER_OPEN = 'datepicker.open';

class DateRangePickerStateless extends Component {
    static contextType = TrackingContext;

    static displayName = 'DateRangePickerStateless';

    static propTypes = {
        /** The location on a page where an action takes place (for edap events) */
        actionLocation: PropTypes.string,
        /** An optional passed in React component, used to render an alert within the date picker pop up. */
        alertTemplateComponent: PropTypes.func,
        /** An optional object, used to pass data to the alert component. */
        alertTemplateData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Optional labels object to override default locale-driven labels */
        calendarLabels: PropTypes.shape({
            dayNames: PropTypes.arrayOf(PropTypes.string),
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            /** For locales that need a year label while displaying date e.g. ja-JP*/
            yearLabel: PropTypes.string,
        }),
        /** Centers tooltip position, true by default */
        centered: PropTypes.bool,
        /** The text label displayed on the clear button */
        clearButtonLabel: PropTypes.string,
        /** The text label displayed on the close button */
        closeButtonLabel: PropTypes.string,
        /** Boolean to determine whether the tooltip will close when the user clicks outside of the tooltip. If true, the tooltip will close  and clear immediately on a click outside. If false, the tooltip will not close and clear on a click outside until either a start and end date are selected or date selections are cleared. Default is true. */
        closeOnOutsideClick: PropTypes.bool,
        /** Function that updates isOpen state and closes the date picker. */
        closeCalendar: PropTypes.func,
        /** Consolidate check in and check out fields into a single input field */
        consolidateInputs: PropTypes.bool,
        /** The text label displayed on the continue button */
        continueButtonLabel: PropTypes.string,
        /** Options object, eg. {month: 'short', day: 'numeric'} (See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString) */
        dateFormatOptions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** DayData that will power any passed in template */
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** An optional passed in React component, used to render each day. */
        dayTemplateComponent: PropTypes.func,
        /** Prevents keyboard input. Useful on iPad to prevent software keyboard from popping up. */
        disableTextInput: PropTypes.bool,
        /** Flag that specifies whether year should be displayed first, useful in locales like ja-JP*/
        displayYearFirst: PropTypes.bool,
        /** Enable date range being the same date. By default, if the end date is selected to be the same as the start date, the entire selection is canceled */
        enableSameDayDateRange: PropTypes.bool,
        /** Selected start date */
        endDate: propTypeDateString,
        /** Text to display in the end date input when an end date is selected. If not specified, the selected end date is displayed. */
        endDateText: PropTypes.string,
        /** Form input error message */
        errorMsg: PropTypes.string,
        /** Flag to determine if there is a fade transition when the tooltip closes. True by default. */
        fadeOut: PropTypes.bool,
        /** FormInput's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** Add content to the left of the input 1. This can be a string, element, or component */
        inputIcon1: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element,
            PropTypes.func,
        ]),
        /** Add content to the left of the input 2. This can be a string, element, or component */
        inputIcon2: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element,
            PropTypes.func,
        ]),
        /** Form input label for the start date input */
        inputLabel1: PropTypes.node,
        /** Form input label for the end date input */
        inputLabel2: PropTypes.node,
        /** Form input name for the start date input*/
        inputName1: isRequiredIf(PropTypes.string, (props) => !props.isInline),
        /** Form input name for the end date input*/
        inputName2: PropTypes.string,
        /** Flag to signify the date picker is still in the process of closing. */
        isClosing: PropTypes.bool,
        /** Vertically scrolling calendar that can be embedded on desktop. Eg: In the ChatBot sidebar */
        isInline: PropTypes.bool,
        /** Determines whether the date picker is open or not */
        isOpen: PropTypes.bool,
        /** Object containing aria label translations for the current locale. Required for a11y. */
        i18n: PropTypes.shape({
            /** It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. */
            ariaLabelDatesCleared: PropTypes.string,
            ariaLabelCheckinCleared: PropTypes.string,
            ariaLabelCheckinSelected: PropTypes.string,
            ariaLabelCheckoutCleared: PropTypes.string,
            ariaLabelCheckoutSelected: PropTypes.string,
            ariaLabelClose: PropTypes.string,
            ariaLabelNextButton: PropTypes.string,
            ariaLabelNotValidCheckin: PropTypes.string,
            ariaLabelNotValidCheckout: PropTypes.string,
            ariaLabelPrevButton: PropTypes.string,
            ariaLabelUnavailable: PropTypes.string,
            /** The aria label used for screen readers to describe how to use the date picker. */
            directions: PropTypes.string,
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** Maximum allowable date after which the next button will be disabled; scrollable mode stops loading more months. */
        maxDate: PropTypes.instanceOf(Date),
        /** Minimum allowable date before which the prev button will be disabled; scrollable mode uses month and year props. */
        minDate: PropTypes.instanceOf(Date),
        /** There are three different mobile behaviors (2 are only for use with the affiliates program), 1) scrolling - switches to a vertical scrolling list. Currently capped at 24 months. 2) single-affiliates - special single month mode for affiliates program (fits within 392px height). 3) double-affiliates - special double month mode for affiliates program (fits within 392px height). */
        mobileBehavior: PropTypes.oneOf([
            'scrolling',
            'single-affiliates',
            'double-affiliates',
        ]),
        /* Custom title for modal */
        modalTitle: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        /** Options callback when date picker is closed (X, Esc) */
        onCancel: PropTypes.func,
        /** Function to call when a date is selected on the calendar */
        onDateRangeChange: PropTypes.func,
        /** Function to call when dates are cleared */
        onDateRangeClear: PropTypes.func,
        /** An optional passed in React component, used to trigger Next Available arrival and departure dates when clicked on disabled dates. **/
        onDisabledDayClick: PropTypes.func,
        /** Function that is called once the date picker has completed closing. */
        onFinishClose: PropTypes.func,
        /** Optional callback for when the date picker gains focus */
        onFocus: PropTypes.func,
        /** onHoverDay(date) Triggered when hoverDate is changed.
         * Used to set endDate in alertTemplateData according to hoverDate  */
        onHoverDay: PropTypes.func,
        /** Optional callback for when the date picker opens */
        onOpen: PropTypes.func,
        /** Optional callback when date picker toggles between selecting startDate and endDate, passes true as a param if selecting end date */
        onSelectionModeChange: PropTypes.func,
        /** Optional callback when date picker is submitted (continue) */
        onSubmit: PropTypes.func,
        /** Function used to open the date picker. */
        openCalendar: PropTypes.func,
        /** The DOM element to use as the parent for the date range picker when visible. This is useful in scenarios where the date range picker needs to break out of its current container due to overflow or z-index issues. */
        parentNode: DatePickerBase.checkElementType,
        /** Defines placement of tooltip. default: 'bottom' */
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        /** Determines which side of the input field inputIcons1 shows. Positions right if true. */
        positionIconRight: PropTypes.bool,
        /** Display-only mode */
        readOnly: PropTypes.bool,
        /** The DOM element that the date range picker needs to scroll with. Only specify this if parentNode was specified and the date picker is displayed in a container that is scrollable independent of the browser window. */
        scrollNode: DatePickerBase.checkElementType,
        /** Value that describes what portion of the dates are being changed. */
        selectionChangeMode: PropTypes.oneOf(['all', 'end', 'start']),
        /** Controls whether an arrow is displayed between the begin and end date inputs */
        showArrow: PropTypes.bool,
        /** Show "continue" button in modal on mobile */
        showContinueButton: PropTypes.bool,
        /** Controls whether begin and end date inputs are separated visually */
        splitInputs: PropTypes.bool,
        /** Selected start date */
        startDate: propTypeDateString,
        /** Text to display in the start date input when a start date is selected. If not specified, the selected start date is displayed. */
        startDateText: PropTypes.string,
        /** Function to update selectionChangeMode state. */
        updateSelectionChangeMode: PropTypes.func,
        /** By default, the input elements are given a data-wdio attribute of "date-range-input-start" and "date-range-input-end".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string,
    };

    static defaultProps = {
        actionLocation: 'top',
        calendarLabels: {
            yearLabel: '',
        },
        centered: true,
        closeCalendar: () => {},
        closeOnOutsideClick: true,
        consolidateInputs: false,
        dateFormatOptions: {},
        disableTextInput: false,
        displayYearFirst: false,
        enableSameDayDateRange: false,
        fadeOut: true,
        i18n: {
            ariaLabelCheckinCleared: 'Check-in date cleared.',
            ariaLabelCheckinSelected: '{0} is selected as your check-in date.',
            ariaLabelCheckoutCleared: 'Checkout date cleared.',
            ariaLabelCheckoutSelected: '{0} is selected as your checkout date.',
            ariaLabelClose:
                'Close the Date Picker or proceed to select a date.',
            ariaLabelDatesCleared: 'Dates cleared.',
            ariaLabelNextButton: 'Next month',
            ariaLabelNotValidCheckin: '{0} is not a valid check in date.',
            ariaLabelNotValidCheckout: '{0} is not a valid checkout date.',
            ariaLabelPrevButton: 'Previous month',
            ariaLabelUnavailable: '{0} is unavailable.',
            directions:
                'Check in date expanded. Choose a date from the table below. Tab into the table and use the arrow keys for navigation.',
        },
        isClosing: false,
        mobileBehavior: 'scrolling',
        onCancel: () => {},
        onDateRangeChange: () => {},
        onDateRangeClear: () => {},
        onFocus: () => {},
        onOpen: () => {},
        onSelectionModeChange: () => {},
        onSubmit: () => {},
        openCalendar: () => {},
        placement: 'bottom',
        positionIconRight: false,
        readOnly: false,
        showArrow: true,
        showContinueButton: false,
        splitInputs: false,
        updateSelectionChangeMode: () => {},
    };

    constructor(props) {
        super(props);

        this.hasFocus = false;
    }

    componentDidMount() {
        window.addEventListener('blur', this.windowBlur);
    }

    componentWillUnmount() {
        // Cleanup listener added in componentDidMount.
        window.removeEventListener('blur', this.windowBlur);
    }

    // This logic is necessary to appropriately open a mobile DateRangePicker
    // with access to all the focusable elements. (For tab a11y)
    componentDidUpdate(prevProps) {
        const {isMobile, isOpen, selectionChangeMode} = this.props;
        if (isMobile && isOpen && !prevProps.isOpen) {
            if (selectionChangeMode === 'end') {
                this.handleFocusInput2();
            } else {
                this.handleFocusInput1();
            }
        }
    }

    windowBlur = () => {
        // When a user switches between tabs in the browser, the last focussed
        // element will be re-focussed by the browser. If the date picker input
        // was the last focussed element, this behavior causes the date picker
        // to incorrectly re-open when switching tabs. So, keep track of the
        // currently focussed element on window blur in order to determine if
        // it has actually changed.
        this.prevActiveElement = document.activeElement;
    };

    getTargetFromEvent(e) {
        const isDocument = typeof document !== 'undefined';
        let eventTarget = e ? e.target || e.relatedTarget : null;

        //  IE11 sets document.activeElement to the next focused element
        //  before the blur event is called.
        if (
            isDocument &&
            !!window.MSInputMethodContext &&
            !!document.documentMode
        ) {
            eventTarget = null;
        }
        return (
            eventTarget ||
            (isDocument && document.hasFocus() && document.activeElement) ||
            null
        );
    }

    handleBlur = (e) => {
        const target = this.getTargetFromEvent(e);
        // When the input loses focus, clear out the variable that tracks the
        // last active element.
        this.prevActiveElement = null;

        if (this.props.isOpen && !this.isWithin(target)) {
            this.hasFocus = false;
            this.handleClose();
        }
    };

    handleFocusInput1 = (e) => {
        const {endDate, isClosing, isOpen, startDate} = this.props;
        const target = this.getTargetFromEvent(e);
        const className = this.props.consolidateInputs
            ? 'form-control--arrive-depart'
            : 'form-control--arrival';

        this.hasFocus = true;

        if (isOpen && startDate && !endDate) {
            this.props.onDateRangeChange('', '');
        }

        if (!isClosing && this.prevActiveElement !== target) {
            this.props.openCalendar('start', className);
        }
    };

    handleFocusInput2 = () => {
        const {isClosing, startDate} = this.props;

        if (!startDate) {
            this.handleFocusInput1();
        } else if (!isClosing) {
            this.props.openCalendar('end', 'form-control--departure'); // Passes the selectionChangeMode and formControlClass
        }
    };

    // Callback to continue handling the opening process after the modal or tooltip has opened
    handleOpen = () => {
        const {actionLocation, isMobile, isOpen, onOpen} = this.props;

        if (isOpen) {
            onOpen();

            if (!isMobile && document && document.documentElement) {
                // Listen for clicks outside this component. Done via a setTimeout
                // in order to ignore the initial bubble of the click that initiated
                // the open.
                window.setTimeout(() => {
                    document.documentElement.addEventListener(
                        'mouseup',
                        this.handleWindowClick
                    );
                    document.documentElement.addEventListener(
                        'touchstart',
                        this.handleWindowClick
                    );
                }, 0);
            }

            const fields = {
                actionlocation: actionLocation,
            };
            if (this.context.hasProvider) {
                this.context.trigger(EVENT_PICKER_OPEN, fields);
            } else {
                emitDatePickerEvent(EVENT_PICKER_OPEN, fields);
            }
        }
    };

    contains(elem, target) {
        return !!elem && findDOMNode(elem).contains(target);
    }

    isWithin = (target) => {
        return (
            this.contains(this.input1, target) ||
            this.contains(this.input2, target) ||
            this.contains(this.overlay1, target) ||
            this.contains(this.overlay2, target) ||
            this.contains(this.calendar, target)
        );
    };

    handleWindowClick = (event) => {
        // We want to close the tooltip when the user clicks anything on the screen,
        // outside of the tooltip. This is for non-mobile.
        if (this.isWithin(event.target)) {
            event.stopImmediatePropagation();
        }
    };

    // eslint-disable-next-line complexity
    handleDateChange = (startDateString, endDateString) => {
        const {
            onDateRangeChange,
            isMobile,
            actionLocation,
            updateSelectionChangeMode,
        } = this.props;

        onDateRangeChange(startDateString, endDateString);

        if (endDateString && !isMobile) {
            // Delaying to allow selection visual to appear before closing
            window.setTimeout(() => {
                // Selection and close of the picker is a "submit" so pass the
                // handleSubmit function to handleClose so it will be invoked.
                this.handleClose(this.handleSubmit, endDateString);
            }, 20);
        } else if (endDateString) {
            updateSelectionChangeMode('start');
        } else if (!startDateString) {
            updateSelectionChangeMode('all');
        } else {
            updateSelectionChangeMode('end');
        }

        if (endDateString) {
            const fields = {
                actionlocation: actionLocation,
                eventstartdate: startDateString,
                eventenddate: endDateString,
            };
            if (this.context.hasProvider) {
                this.context.trigger(EVENT_DATES_SELECTED, fields);
            } else {
                emitDatePickerEvent(EVENT_DATES_SELECTED, fields);
            }
        }
    };

    handleClearDates = (event) => {
        const {
            onDateRangeChange,
            onDateRangeClear,
            updateSelectionChangeMode,
        } = this.props;
        event.preventDefault();
        onDateRangeChange(null, null);

        this.triggerClearEvent();

        updateSelectionChangeMode('all');
        onDateRangeClear();
    };

    // Due to a11y, onKeydown is handled differently than the onClick for the clear button.
    // The clear button disables itself after being pressed, which then moves focus to the document body by browser default.
    // To prevent us from losing focus to the body, we reset the focused element to the first
    // focusable element in DateRangePicker before performing the clear.
    handleKeydownClearDates = (e, ref) => {
        const {
            onDateRangeChange,
            onDateRangeClear,
            updateSelectionChangeMode,
        } = this.props;
        const keycode = e.which;

        if (keycode === 13 || keycode === 32) {
            // Enter key or spacebar
            e.preventDefault();
            e.stopPropagation(); // Prevents the initial keydown on the clear button from progating to the new focused element

            ref.resetFocus();

            onDateRangeChange(null, null);

            this.triggerClearEvent();

            updateSelectionChangeMode('all');
            onDateRangeClear();
        }
    };

    triggerClearEvent = () => {
        const fields = {
            actionlocation: this.props.actionLocation,
        };

        if (this.context.hasProvider) {
            this.context.trigger(EVENT_DATES_CLEARED, fields);
        } else {
            emitDatePickerEvent(EVENT_DATES_CLEARED, fields);
        }
    };

    handleClose = (callback, selectedEndDate) => {
        const {isMobile, isOpen, startDate, endDate} = this.props;

        if (isOpen) {
            if (startDate && !selectedEndDate && !endDate) {
                this.props.onDateRangeChange('', '');
            }

            this.props.closeCalendar(() => this.closeCallback(callback));

            if (!isMobile && document && document.documentElement) {
                // Stop listening for clicks outside of the tooltip
                document.documentElement.removeEventListener(
                    'mouseup',
                    this.handleWindowClick
                );
                document.documentElement.removeEventListener(
                    'touchstart',
                    this.handleWindowClick
                );
            }
        }
    };

    // eslint-disable-next-line complexity
    closeCallback = (externalCallback) => {
        const {
            actionLocation,
            consolidateInputs,
            endDate,
            isMobile,
            startDate,
        } = this.props;

        const fields = {
            actionlocation: actionLocation,
        };
        if (this.context.hasProvider) {
            this.context.trigger(EVENT_PICKER_CLOSE, fields);
        } else {
            emitDatePickerEvent(EVENT_PICKER_CLOSE, fields);
        }

        // Used for this.props.onCancel and this.props.onSubmit optional callbacks
        if (externalCallback) {
            externalCallback();
        }

        // We need to wait until after closeCalendar() completes updating the state
        // before moving focus to the input so we do not cause a second calendar to open
        if (isMobile) {
            if (consolidateInputs || !startDate || (startDate && !endDate)) {
                this.input1.focus();
            }
        }

        if (!isMobile && this.hasFocus) {
            // Force focus to overlay element due to prevent SR reading aria-label again
            this.overlay1.setAttribute('tabindex', 0);
            this.overlay1.focus();
            this.overlay1.removeAttribute('tabindex');
        }
    };

    handleChangeInput1 = () => {
        // TODO: allow keyboard entry
    };

    handleChangeInput2 = () => {
        // TODO: allow keyboard entry
    };

    handleSubmit = () => {
        this.props.onSubmit();
    };

    calendarRefFunc = (c) => {
        this.calendar = c;
    };

    getWdio(prefix, elementClass) {
        return prefix ? `${prefix}-${elementClass}` : elementClass;
    }

    // eslint-disable-next-line
    renderInputs() {
        const {
            startDate,
            startDateText,
            endDate,
            endDateText,
            i18n,
            id,
            isInline,
            isMobile,
            isOpen,
            inputLabel1,
            inputLabel2,
            inputName1,
            inputName2,
            positionIconRight,
            inputIcon1,
            inputIcon2,
            errorMsg,
            readOnly,
            wdioPrefix,
            locale,
            consolidateInputs,
            dateFormatOptions,
            showArrow,
            splitInputs,
        } = this.props;

        const firstInputId = `input1-${id}`;
        const secondInputId = `input2-${id}`;
        const isShowArrow = showArrow && !consolidateInputs && !splitInputs;
        const isSplitInputs = splitInputs && !consolidateInputs;

        const leftContent = !positionIconRight ? inputIcon1 : null;
        const rightContent = positionIconRight ? inputIcon1 : null;

        const leftContent2 = !positionIconRight ? inputIcon2 : null;
        const rightContent2 = positionIconRight ? inputIcon2 : null;

        const focus01 = isOpen && this.props.selectionChangeMode !== 'end';
        const focus02 = isOpen && this.props.selectionChangeMode === 'end';

        const input1Classes = classNames({'not-empty': focus01});
        const input2Classes = classNames({'not-empty': focus02});

        const input1InputClasses = classNames({
            'form-control--arrival': !consolidateInputs,
            'form-control--arrive-depart': consolidateInputs,
            'DateRangePicker__input--mimic-focus': consolidateInputs
                ? isOpen
                : focus01,
            'DateRangePicker__input--margined': isSplitInputs,
        });

        const input2InputClasses = classNames(
            'form-control--departure DateRangePicker__FormInput-departure',
            {
                'DateRangePicker__input--mimic-focus': focus02,
                'form-control--no-arrow': !isShowArrow,
                'DateRangePicker__input--margined': isSplitInputs,
            }
        );

        const overlayClasses = classNames('picker__overlay', {
            'picker__overlay--selectable': !readOnly,
        });

        const formGroupClasses = classNames(
            'form-group',
            'position-relative',
            'form-combined__input1',
            {
                'DateRangePicker__form-group--margined': isSplitInputs,
            }
        );

        const inputValue1 = consolidateInputs
            ? getEffectiveConsolidatedDateString(
                  startDate,
                  startDateText,
                  endDate,
                  endDateText,
                  locale,
                  dateFormatOptions
              )
            : getEffectiveDateText(
                  startDate,
                  startDateText,
                  locale,
                  dateFormatOptions
              );
        const inputValue2 = getEffectiveDateText(
            endDate,
            endDateText,
            locale,
            dateFormatOptions
        );

        return (
            <div className="form-combined-row">
                <div className={formGroupClasses}>
                    <FormInput
                        className={input1Classes}
                        inputClassName={input1InputClasses}
                        name={inputName1}
                        label={isInline ? '' : inputLabel1}
                        value={inputValue1}
                        onChange={this.handleChangeInput1}
                        onFocus={readOnly ? null : this.handleFocusInput1}
                        onBlur={isMobile || readOnly ? null : this.handleBlur}
                        errorMsg={errorMsg}
                        ref={(c) => (this.input1 = c)}
                        readOnly={'readOnly'}
                        data-wdio={this.getWdio(
                            wdioPrefix,
                            'date-range-input-start'
                        )}
                        leftContent={leftContent}
                        rightContent={rightContent}
                        aria-label={readOnly ? '' : i18n.directions}
                        id={firstInputId}
                    />
                    <div
                        className={overlayClasses}
                        ref={(c) => (this.overlay1 = c)}
                        onClick={readOnly ? null : this.handleFocusInput1}
                    />
                </div>

                {isShowArrow && (
                    <div className="form-group form-combined__svg" aria-hidden>
                        <SVGIcon markup={ARROW_RIGHT__24} />
                    </div>
                )}

                {!consolidateInputs && (
                    <div
                        className={
                            'form-group position-relative form-combined__input2'
                        }
                        aria-hidden
                    >
                        <FormInput
                            className={input2Classes}
                            inputClassName={input2InputClasses}
                            name={inputName2}
                            label={isInline ? '' : inputLabel2}
                            value={inputValue2}
                            onChange={this.handleChangeInput2}
                            onFocus={readOnly ? null : this.handleFocusInput2}
                            ref={(c) => (this.input2 = c)}
                            readOnly={'readOnly'}
                            data-wdio={this.getWdio(
                                wdioPrefix,
                                'date-range-input-end'
                            )}
                            leftContent={leftContent2}
                            rightContent={rightContent2}
                            tabIndex={-1}
                            id={secondInputId}
                        />
                        <div
                            className={overlayClasses}
                            ref={(c) => (this.overlay2 = c)}
                            onClick={readOnly ? null : this.handleFocusInput2}
                        />
                    </div>
                )}
            </div>
        );
    }

    render() {
        const classes = classNames(
            this.props.className,
            'date-range form-combined form-combined--dates',
            {
                'date-range--inline': this.props.isInline,
            }
        );

        return (
            <DatePickerBase
                alertTemplateComponent={this.props.alertTemplateComponent}
                alertTemplateData={this.props.alertTemplateData}
                calendarLabels={this.props.calendarLabels}
                calendarRefFunc={this.calendarRefFunc}
                centered={this.props.centered}
                className={classes}
                clearButtonLabel={this.props.clearButtonLabel}
                closeButtonLabel={this.props.closeButtonLabel}
                closeOnOutsideClick={this.props.closeOnOutsideClick}
                continueButtonLabel={this.props.continueButtonLabel}
                dayData={this.props.dayData}
                dayTemplateComponent={this.props.dayTemplateComponent}
                displayYearFirst={this.props.displayYearFirst}
                enableSameDayDateRange={this.props.enableSameDayDateRange}
                endDate={this.props.endDate}
                fadeOut={this.props.fadeOut}
                inputs={this.renderInputs()}
                isInline={this.props.isInline}
                isDelayed={this.props.isDelayed}
                isMobile={this.props.isMobile}
                isOpen={this.props.isOpen}
                i18n={this.props.i18n}
                locale={this.props.locale}
                maxDate={this.props.maxDate}
                minDate={this.props.minDate}
                mobileBehavior={this.props.mobileBehavior}
                modalTitle={this.props.modalTitle}
                month={this.props.month}
                numMonthsToDisplay={2}
                onCancel={this.props.onCancel}
                onClose={this.handleClose}
                onHoverDay={this.props.onHoverDay}
                onDatesCleared={this.handleClearDates}
                onDisabledDayClick={this.props.onDisabledDayClick}
                onFinishClose={this.props.onFinishClose}
                onFocus={this.props.onFocus}
                onKeydownDatesCleared={this.handleKeydownClearDates}
                onOpen={this.handleOpen}
                onSelectionChanged={this.handleDateChange}
                onSubmit={this.handleSubmit}
                parentNode={this.props.parentNode}
                placement={this.props.placement}
                scrollNode={this.props.scrollNode}
                selectionChangeMode={this.props.selectionChangeMode}
                selectionType="range"
                showContinueButton={this.props.showContinueButton}
                startDate={this.props.startDate}
                year={this.props.year}
            />
        );
    }
}

export default DateRangePickerStateless;
